import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, throwError } from 'rxjs';
import { SessionExpiredService } from '../../../../attachments-handler/src/lib/services/session-expired.service';
import { SKIP_ERROR_TOAST } from '../../../../attachments-handler/src/lib/http/http-context-tokens';
import { captureHttpError } from '../monitoring/sentry';

interface ApiError {
  message: string;
  error_code: string;
  context?: Record<string, unknown>;
}

const ERROR_MESSAGES: Record<string, string> = {
  linked_file_cannot_be_modified:
    'A(z) {filename} fájl nem módosítható: {linksCount} aktív link kapcsolódik hozzá.',
  file_not_found:
    'A(z) {filename} fájl nem található.',
  folder_not_found:
    'A(z) {directory} mappa nem található.',
  permission_denied:
    'Nincs jogosultsága ehhez a művelethez.',
  file_already_exists:
    'A(z) {filename} fájl már létezik.',
  folder_already_exists:
    'A(z) {directory} mappa már létezik.',
  directory_create_failed:
    'A "{name}" mappa neve érvénytelen karaktert tartalmaz.',
  upload_failed:
    'A fájl feltöltése sikertelen.',
  delete_failed:
    'A törlés sikertelen.',
  rename_failed:
    'Az átnevezés sikertelen.',
  file_move_failed:
    'A fájl áthelyezése sikertelen: már létezik fájl ezzel a névvel a célmappában.',
  directory_move_failed:
    'A mappa áthelyezése sikertelen: már létezik mappa ezzel a névvel a célmappában.',
  file_not_modified:
    'A(z) {filename} fájl nem frissült: a feltöltött tartalom megegyezik a tárolton lévővel.',
  file_conflict:
    'A(z) {filename} fájl ütközést okozott.',
  unsupported_media_type:
    'A(z) {filename} fájl típusa ({mimeType}) nem engedélyezett.',

  docmatrix_not_found:
    'A Mátrix nem található!',
  docmatrix_read_only:
    'A Mátrix nem módosítható!',
  unlink_failed_version_mismatch:
    'A link verziója és a Mátrix aktuális verziója eltérő! Csak aktuális verzió link-je törölhető!'
};

function interpolate(template: string, context: Record<string, unknown>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) =>
    key in context ? String(context[key]) : `{${key}}`
  );
}

function resolveMessage(apiError: ApiError): string {
  const template = ERROR_MESSAGES[apiError.error_code];
  if (template) {
    return interpolate(template, apiError.context ?? {});
  }
  return 'A művelet sikertelen.';
}

type Severity = 'info' | 'warn' | 'error';

function httpStatusMeta(status: number): { summary: string; severity: Severity } {
  switch (status) {
    case 304: return { summary: 'Nem frissült',            severity: 'info'  };
    case 404: return { summary: 'Nem található',           severity: 'error' };
    case 403: return { summary: 'Hozzáférés megtagadva',   severity: 'error' };
    case 409: return { summary: 'Ütközés',                 severity: 'warn'  };
    case 413: return { summary: 'Nem támogatott fájlformátum', severity: 'warn' };
    case 415: return { summary: 'Nem támogatott fájltípus', severity: 'warn' };
    case 422: return { summary: 'Érvénytelen adat',        severity: 'error' };
    case 423: return { summary: 'Zárolva',                 severity: 'warn'  };
    default:  return status >= 500
      ? { summary: 'Szerverhiba', severity: 'error' }
      : { summary: 'Hiba',        severity: 'error' };
  }
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);
  const sessionExpired = inject(SessionExpiredService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 0 || err.status >= 500) {
        captureHttpError(err, req.method, req.url);
      }

      if (err.status === 401) {
        sessionExpired.markExpired();
        return throwError(() => err);
      }

      if (req.context.get(SKIP_ERROR_TOAST)) {
        return throwError(() => err);
      }

      const apiError: ApiError | null =
        err.error && typeof err.error === 'object' && 'error_code' in err.error
          ? (err.error as ApiError)
          : null;

      let detail = apiError ? resolveMessage(apiError) : 'Ismeretlen hiba történt.';
      let { summary, severity } = httpStatusMeta(err.status);

      //Handle 304 Not modified
      if(err.status === 304) {
        detail = 'A fájl nem frissült! A tartalma megegyezik a tárolón lévővel!';
      }

      messageService.add({ severity, summary, detail, life: 6000 });

      return throwError(() => err);
    })
  );
};
